import { checkFeatureFlagGate } from '@wac-web/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@wac-web/utils/feature-flags/feature-flag-manifest';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import BookIcon from '@atlaskit/icon/core/book-with-bookmark';
import CompassIcon from '@atlaskit/icon/core/compass';
import PersonIcon from '@atlaskit/icon/core/person-avatar';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';

import { useRoutes } from '@/common/hooks';
import { FASTSHIFT_PATH_KEYS } from '@/common/routes/constants';
import { getDomainScopedPathKeys } from '@/common/routes/utils';
import { useFastshiftSelector } from '@/state';

import { messages } from '../messages';
import { canUserAccessRoutePolicy, FASTSHIFT_ROUTE_ACCESS_POLICIES } from '../route-access-policy';

type NavLinkItem = {
	key: FASTSHIFT_PATH_KEYS;
	label: string;
	icon: React.ReactElement;
	link: string | null;
};

export const usePageNavLinks = () => {
	const { fullPathname, paths } = useRoutes();
	const { formatMessage } = useIntl();
	const { domainId } = useParams<{ domainId: string }>();
	const { data: user } = useFastshiftSelector((state) => state.userAccess);

	const { data: selectedDomainData } = useFastshiftSelector((state) => state.customerDomain);

	const { data: domainsTableList } = useFastshiftSelector((state) => state.customerDomains);

	const isUnder1kExperienceEnabled = checkFeatureFlagGate(
		featureFlags.sxpFastshiftPortalUnder1kCustomerExperience,
	);

	const isOrganizationRedesignEnabled = checkFeatureFlagGate(
		featureFlags.sxpFspOrganizationRedesign,
	);

	const canAccessOrganizations =
		Boolean(user?.hasMoveTicketAccess) && isOrganizationRedesignEnabled;

	// note that the order of this navLinks array determines
	// the order of links displayed inside the sidenav component
	const navLinks: NavLinkItem[] = [
		// (Ordered) Partner Scoped Links
		{
			key: FASTSHIFT_PATH_KEYS.SELECTION,
			label: formatMessage(messages.customersNavLabel),
			link: paths.selection,
			icon: <PersonIcon label="" />,
		},
		{
			key: FASTSHIFT_PATH_KEYS.RESOURCES_PARTNER_SCOPED,
			label: formatMessage(messages.resourcesNavLabel),
			link: paths.partnerResources,
			icon: <BookIcon label="" />,
		},
		// (Ordered) Domain Scoped Links
		{
			key: FASTSHIFT_PATH_KEYS.OVERVIEW,
			label: formatMessage(messages.forYouNavLabel),
			link: paths.domainScopedOverview,
			icon: <PersonIcon label="" />,
		},
		{
			key: FASTSHIFT_PATH_KEYS.TICKETS,
			label: formatMessage(messages.migrationTicketsNavLabel),
			link: paths.domainScopedTickets,
			icon: <WorkItemsIcon label="" />,
		},
		{
			key: FASTSHIFT_PATH_KEYS.PLANNING,
			label: formatMessage(messages.migrationPlanningNavLabel),
			link: paths.domainScopedPlanning,
			icon: <CompassIcon label="" />,
		},
		{
			key: FASTSHIFT_PATH_KEYS.RESOURCES_DOMAIN_SCOPED,
			label: formatMessage(messages.resourcesNavLabel),
			link: paths.domainScopedResources,
			icon: <BookIcon label="" />,
		},
	];

	const visibleNavLinks = navLinks.filter((navLink) =>
		canUserAccessRoutePolicy(FASTSHIFT_ROUTE_ACCESS_POLICIES[navLink.key], user),
	);

	// find the key of the navlinks object with a link that matches the pathname
	const currentPageKey: FASTSHIFT_PATH_KEYS | undefined = visibleNavLinks.find(
		(navLink) => navLink.link === fullPathname,
	)?.key as FASTSHIFT_PATH_KEYS;

	// filter and return domain scoped sidenav
	// if customer is small (under 1k), do not show "Migration Planning" navlink
	const domainScopedPathKeys = getDomainScopedPathKeys();

	const userSelectedAnOver1kCustomerFromSelectionPage = domainsTableList?.data?.find(
		(domain) => domain.customerDomainId === domainId,
	)?.hasOver1KPaidSeatsInAtLeastOneProduct;

	const shouldShowMigrationPlanningLink =
		selectedDomainData?.hasOver1KPaidSeatsInAtLeastOneProduct ||
		userSelectedAnOver1kCustomerFromSelectionPage;

	const domainScopedLinks = visibleNavLinks.filter(
		(navLink) =>
			domainScopedPathKeys.includes(navLink.key) &&
			(navLink.key !== FASTSHIFT_PATH_KEYS.PLANNING || shouldShowMigrationPlanningLink),
	);

	if (domainScopedPathKeys.includes(currentPageKey || '')) {
		return {
			pageKey: currentPageKey,
			links: isUnder1kExperienceEnabled
				? domainScopedLinks
				: [...visibleNavLinks.filter((navLink) => domainScopedPathKeys.includes(navLink.key))],
			isOnDomainScopedPage: true,
			canAccessOrganizations,
		};
	}

	// return partner scoped sidenav
	return {
		pageKey: currentPageKey,
		links: [...visibleNavLinks.filter((navLink) => !domainScopedPathKeys.includes(navLink.key))],
		isOnDomainScopedPage: false,
		canAccessOrganizations,
	};
};
